import { useMemo } from 'react';
import dayjs from 'dayjs';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { ServiceProvidersConstants } from '@frontend/api-service-providers';
import { getCalendarStartAndEndDateTime, isValidDate } from '../../utils/date-helpers';
import { useSchedulingLocationInfo } from '../use-scheduling-location-info';

const { ONE_MINUTE_IN_MILLISECONDS } = ServiceProvidersConstants;

const commonQueryOptions = {
  retry: 1,
  cacheTime: 10 * ONE_MINUTE_IN_MILLISECONDS, // 10 mins
  staleTime: 10 * ONE_MINUTE_IN_MILLISECONDS, // 10 mins,
};

type UseGetCalendarEventsV3DataNewParamsType = {
  selectedLocationIds: string[];
  page?: number;
  limit?: number;
  selectedDate: string; // MM/DD/YYYY
  enabled?: boolean;
};

export const useGetCalendarEventsV3Data = ({
  selectedLocationIds,
  selectedDate,
  page,
  limit,
  enabled = true,
}: UseGetCalendarEventsV3DataNewParamsType) => {
  const { isScheduleV3FlagEnabled } = SchedulerV3.Helpers.useGetSchedulerV3FeatureFlagDetails();
  const { isMultiLocation, parentLocationId, getLocationTimeZone } = useSchedulingLocationInfo();

  const locationInfoListMap = useMemo(() => {
    return Object.fromEntries(
      selectedLocationIds.map((locationId) => {
        const locationTimezone = getLocationTimeZone(locationId);
        const { startDateTime, endDateTime } = getCalendarStartAndEndDateTime(selectedDate, locationTimezone);

        return [
          locationId,
          {
            timezone: locationTimezone,
            startDateTime,
            endDateTime,
          },
        ];
      })
    );
  }, [getLocationTimeZone, selectedDate, selectedLocationIds]);

  const queryEnabled =
    enabled && isScheduleV3FlagEnabled && !!selectedLocationIds.length && isValidDate(dayjs(selectedDate));

  const singleLocationId = selectedLocationIds[0];
  const singleLocationStartDateTime = locationInfoListMap[singleLocationId]?.startDateTime;
  const singleLocationEndDateTime = locationInfoListMap[singleLocationId]?.endDateTime;
  const singleLocationQuery = SchedulerV3.Queries.useListCalendarEventsQuery({
    request: {
      locationId: singleLocationId,
      startDateTime: singleLocationStartDateTime,
      endDateTime: singleLocationEndDateTime,
      includeAttendeeDetails: true,
      ...(limit && { limit }),
      ...(page && { page }),
    },
    options: {
      ...commonQueryOptions,
      enabled:
        queryEnabled &&
        !isMultiLocation &&
        !!singleLocationId &&
        !!singleLocationStartDateTime &&
        !!singleLocationEndDateTime,
    },
  });

  const multiLocationQueries = SchedulerV3.Queries.useListCalendarEventsMultiQueries(
    selectedLocationIds.map((locationId) => ({
      request: {
        parentLocationId,
        locationsIds: [locationId],
        startDateTime: locationInfoListMap[locationId]?.startDateTime,
        endDateTime: locationInfoListMap[locationId]?.endDateTime,
        includeAttendeeDetails: true,
        ...(limit && { limit }),
        ...(page && { page }),
      },
      options: {
        ...commonQueryOptions,
        enabled:
          queryEnabled &&
          isMultiLocation &&
          !!locationInfoListMap[locationId].startDateTime &&
          !!locationInfoListMap[locationId].endDateTime,
      },
    }))
  );

  const isFetchingMultiEvents = multiLocationQueries.some((query) => query.isFetching);

  const computedMultiLocationData = useMemo(() => {
    const initial: SchedulerV3.Types.ListCalendarEventsMultiIO['output'] = { totalCount: 0, events: [] };
    const data = multiLocationQueries.reduce((acc, res) => {
      if (res.data) {
        acc.totalCount = (acc.totalCount || 0) + (res.data.totalCount || 0);
        acc.events = acc.events.concat(res.data.events || []);
      }
      return acc;
    }, initial);
    return {
      data,
      isLoading: multiLocationQueries.some((res) => res.isLoading),
      isError: multiLocationQueries.some((res) => res.isError),
      isFetching: isFetchingMultiEvents,
    };
    // NOTE: due to infinite loop issue, we were not using multiLocationQueries here directly
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetchingMultiEvents, selectedLocationIds, selectedDate]);

  if (!isMultiLocation) {
    return {
      data: singleLocationQuery.data,
      isLoading: singleLocationQuery.isLoading,
      isError: singleLocationQuery.isError,
      isFetching: singleLocationQuery.isFetching,
    };
  }

  return computedMultiLocationData;
};
